import React, { useState } from 'react';
import { Badge, Card, CardBody } from 'reactstrap';

import { Button, ConfirmDialog, ErrorResponse, Spinner } from '~components';
import { useToast } from '~libs/toast';
import { getErrorMessage } from '~libs/axiosWrapper';
import { useGetConnectionsQuery, useDisconnectConnectionMutation } from '~direct-debits/actions/connections';
import { useLazyInitiateOAuthQuery } from '~direct-debits/actions/oauth';
import { GocardlessConnection } from '~direct-debits/models';

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ConnectionManager: React.FC = () => {
  const { data: connections, isLoading, isError, error } = useGetConnectionsQuery();
  const [initiateOAuth, { isFetching: isInitiating }] = useLazyInitiateOAuthQuery();
  const [disconnectConnection, { isLoading: isDisconnecting }] = useDisconnectConnectionMutation();
  const { showToast } = useToast();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const activeConnection: GocardlessConnection | null =
    connections?.find((connection) => connection.status === 'active') ?? null;

  const handleConnect = async (initialView: 'login' | 'signup') => {
    try {
      const result = await initiateOAuth({ initial_view: initialView }).unwrap();
      window.location.href = result.url;
    } catch (err) {
      showToast('danger', getErrorMessage(err));
    }
  };

  const handleDisconnectConfirm = async (confirmed: boolean) => {
    setShowDisconnectConfirm(false);
    if (!confirmed || !activeConnection) return;

    try {
      await disconnectConnection(activeConnection.id).unwrap();
      showToast('success', 'GoCardless account disconnected');
    } catch (err) {
      showToast('danger', getErrorMessage(err));
    }
  };

  if (isLoading) {
    return <Spinner loading className="p-5" />;
  }

  if (isError) {
    return <ErrorResponse message={getErrorMessage(error)} />;
  }

  if (activeConnection) {
    return (
      <div data-cy="dd-connection-status">
        <Card className="mt-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h5 className="mb-2">
                  <Badge color="success" className="me-2">
                    Connected
                  </Badge>
                  GoCardless Account
                </h5>
                <div className="text-muted small">
                  {activeConnection.organisation_id && (
                    <div>
                      <strong>Organisation:</strong> {activeConnection.organisation_id}
                    </div>
                  )}
                  <div>
                    <strong>Environment:</strong> {activeConnection.environment}
                  </div>
                  <div>
                    <strong>Connected:</strong> {formatDate(activeConnection.connected_at)}
                  </div>
                </div>
              </div>
              <Button
                color="outline-danger"
                size="sm"
                onClick={() => setShowDisconnectConfirm(true)}
                disabled={isDisconnecting}
                data-cy="dd-disconnect-btn"
              >
                {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </div>
          </CardBody>
        </Card>
        <ConfirmDialog
          isOpen={showDisconnectConfirm}
          onClose={handleDisconnectConfirm}
          messages={[
            'This will revoke the GoCardless connection and remove the stored access token.',
            'You will need to re-authorize to use GoCardless features again.',
          ]}
        />
      </div>
    );
  }

  return (
    <div data-cy="dd-connection-status">
      <Card className="mt-3">
        <CardBody className="text-center py-5">
          <h5 className="mb-3">Connect your GoCardless account</h5>
          <p className="text-muted mb-4">
            Link your GoCardless account to manage direct debit payments through TurnPoint.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              color="primary"
              onClick={() => handleConnect('signup')}
              disabled={isInitiating}
              data-cy="dd-connect-create-btn"
            >
              {isInitiating ? 'Redirecting...' : 'Create New Account'}
            </Button>
            <Button
              color="outline-primary"
              onClick={() => handleConnect('login')}
              disabled={isInitiating}
              data-cy="dd-connect-link-btn"
            >
              {isInitiating ? 'Redirecting...' : 'Link Existing Account'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default ConnectionManager;
